import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Eye, MessageCircle, Share2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ArticleDetail = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [articleId]);

  const fetchArticle = async () => {
    try {
      // Get the specific article
      const res = await axios.get(`${API}/api/news/${articleId}`);
      setArticle(res.data);
      
      // Get related articles (same category or random)
      const allNews = await axios.get(`${API}/api/news?limit=20`);
      const related = allNews.data
        .filter(a => a.id !== articleId)
        .slice(0, 3);
      setRelatedArticles(related);
    } catch (error) {
      console.error('Failed to fetch article:', error);
      toast.error('Article not found');
      navigate('/news');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  // Convert content with markdown-like formatting
  const renderContent = (content) => {
    if (!content) return null;
    
    // Split by double newlines for paragraphs
    const paragraphs = content.split('\n\n');
    
    return paragraphs.map((para, idx) => {
      // Check for headers (## or emoji headers)
      if (para.startsWith('## ')) {
        return (
          <h2 key={idx} className="text-2xl font-bold text-white mt-8 mb-4">
            {para.replace('## ', '')}
          </h2>
        );
      }
      
      // Check for emoji-style headers (🎤, 📊, etc.)
      if (/^[🎤📊🏆💭📜🎹🤖💎🔥📈🌟🎵📍💡⚖️💰📋🥁🔌].+/.test(para)) {
        const lines = para.split('\n');
        const header = lines[0];
        const rest = lines.slice(1).join('\n');
        return (
          <div key={idx} className="mt-6 mb-4">
            <h3 className="text-xl font-bold text-cyan-400 mb-3">{header}</h3>
            {rest && <div className="text-gray-300 whitespace-pre-line">{rest}</div>}
          </div>
        );
      }
      
      // Check for bullet points
      if (para.includes('\n• ') || para.startsWith('• ')) {
        const items = para.split('\n').filter(line => line.trim());
        return (
          <ul key={idx} className="list-none space-y-2 my-4">
            {items.map((item, i) => (
              <li key={i} className="text-gray-300 flex items-start gap-2">
                <span className="text-cyan-400 mt-1">•</span>
                <span>{item.replace(/^• /, '')}</span>
              </li>
            ))}
          </ul>
        );
      }
      
      // Regular paragraph
      return (
        <p key={idx} className="text-gray-300 leading-relaxed mb-4 whitespace-pre-line">
          {para}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-24 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Article not found</h2>
          <Button onClick={() => navigate('/news')}>Back to News</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Back to News Link */}
      <div className="pt-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/news" 
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors py-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>
        </div>
      </div>

      {/* Hero Image */}
      {article.image_url && (
        <div className="w-full max-w-4xl mx-auto px-4">
          <div className="relative aspect-video rounded-2xl overflow-hidden">
            <img 
              src={article.image_url} 
              alt={article.title}
              className="w-full h-full object-cover"
            />
            {/* Category Badge */}
            <span className="absolute top-4 left-4 px-4 py-1.5 bg-cyan-500 text-black text-sm font-bold rounded-full uppercase">
              {article.category || 'News'}
            </span>
          </div>
        </div>
      )}

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
          {article.title}
        </h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm mb-8 pb-6 border-b border-white/10">
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {formatDate(article.created_at)}
          </span>
          {article.artist && (
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {article.artist}
            </span>
          )}
          {article.submitted_by && (
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              By {article.submitted_by}
            </span>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={shareArticle}
            className="ml-auto text-cyan-400 hover:text-cyan-300"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        {/* Main Content */}
        <div className="prose prose-invert prose-lg max-w-none">
          {renderContent(article.content)}
        </div>

        {/* Embeds Section */}
        {(article.spotify_embed || article.youtube_embed || article.soundcloud_url) && (
          <div className="mt-10 pt-8 border-t border-white/10">
            <h3 className="text-xl font-bold text-white mb-4">Listen / Watch</h3>
            <div className="space-y-4">
              {article.youtube_embed && (
                <div className="aspect-video rounded-xl overflow-hidden">
                  <iframe
                    src={article.youtube_embed}
                    title="YouTube video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              {article.spotify_embed && (
                <iframe
                  src={article.spotify_embed}
                  title="Spotify embed"
                  className="w-full h-20 rounded-xl"
                  allow="encrypted-media"
                />
              )}
            </div>
          </div>
        )}

        {/* External Links */}
        {(article.instagram_url || article.apple_music_url || article.soundcloud_url) && (
          <div className="mt-6 flex flex-wrap gap-3">
            {article.instagram_url && (
              <a 
                href={article.instagram_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-medium hover:opacity-90 transition"
              >
                Instagram <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {article.apple_music_url && (
              <a 
                href={article.apple_music_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-full text-sm font-medium hover:opacity-90 transition"
              >
                Apple Music <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {article.soundcloud_url && (
              <a 
                href={article.soundcloud_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-full text-sm font-medium hover:opacity-90 transition"
              >
                SoundCloud <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-white/10">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, idx) => (
                <Link
                  key={idx}
                  to={`/news?tag=${encodeURIComponent(tag)}`}
                  className="px-4 py-2 bg-white/5 hover:bg-cyan-500/20 text-gray-300 hover:text-cyan-400 rounded-full text-sm transition-colors border border-white/10 hover:border-cyan-500/50"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Comments (0)
          </h2>
          <div className="bg-white/5 rounded-xl p-8 text-center">
            <p className="text-gray-400">No comments yet. Be the first to comment!</p>
            <p className="text-gray-500 text-sm mt-2">Coming soon...</p>
          </div>
        </div>

        {/* Related Stories */}
        {relatedArticles.length > 0 && (
          <div className="mt-12 pt-8 border-t border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6">Related Stories</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/news/${related.id}`}
                  className="group block bg-white/5 rounded-xl overflow-hidden hover:bg-white/10 transition-colors"
                >
                  {related.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={related.image_url} 
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-bold text-white group-hover:text-cyan-400 transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-cyan-400 text-sm mt-2">Read more →</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default ArticleDetail;
